import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shirt, X, Search, ArrowLeft } from 'lucide-react';
import { getWardrobe } from '../../services/api';

function hideOnError(e) {
  e.currentTarget.style.display = 'none';
  const ph = e.currentTarget.nextElementSibling;
  if (ph) ph.style.display = 'flex';
}

export default function Module06({ data }) {
  const [tab, setTab] = useState('looks');

  return (
    <div className="space-y-5">
      <div className="flex gap-5 border-b border-white/8 pb-3">
        {[{ key: 'looks', label: "Today's Looks" }, { key: 'wardrobe', label: 'Wardrobe' }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="text-[9px] tracking-[0.28em] uppercase transition-colors pb-1 -mb-[13px] border-b"
            style={tab === key
              ? { color: 'var(--gold)', borderColor: 'var(--gold)' }
              : { color: '#555', borderColor: 'transparent' }}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'looks' ? <Looks data={data} /> : <Wardrobe />}
    </div>
  );
}

/* ── Looks: pick an occasion, then pick a look ───────────────────── */

function Looks({ data }) {
  const looks = data?.looks || [];
  const occasions = data?.occasions || [];
  const [occasion, setOccasion] = useState(null);
  const [openId, setOpenId] = useState(null);

  // Hooks must run before any early return, so this sits above the empty check.
  const counts = useMemo(() => {
    const c = {};
    looks.forEach((l) => { c[l.occasion] = (c[l.occasion] || 0) + 1; });
    return c;
  }, [looks]);

  // Older static output had `outfits` instead of `looks`.
  if (!looks.length) {
    return (
      <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10">
        Looks are generated each morning at 5:30.
      </p>
    );
  }

  const shown = occasion ? looks.filter((l) => l.occasion === occasion) : looks;
  const open = shown.find((l) => l.id === openId);

  if (open) return <LookDetail look={open} onBack={() => setOpenId(null)} />;

  return (
    <div className="space-y-4">
      <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">
        {data.wardrobe_size} pieces · tap a look for the full outfit
      </p>

      {/* Occasion filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        <Chip active={!occasion} onClick={() => setOccasion(null)}>All {looks.length}</Chip>
        {occasions.map((o) => (
          <Chip key={o.key} active={occasion === o.key} onClick={() => setOccasion(o.key)}>
            {o.label} {counts[o.key] || 0}
          </Chip>
        ))}
      </div>

      {/* Look cards — photo forward, two per row */}
      <div className="grid grid-cols-2 gap-3">
        {shown.map((l) => (
          <button
            key={l.id}
            onClick={() => setOpenId(l.id)}
            className="text-left border border-white/10 hover:border-white/25 active:opacity-70 transition-all"
          >
            <div className="relative aspect-[3/4] overflow-hidden bg-white/3">
              {l.jacket?.thumb_url && (
                <img src={l.jacket.thumb_url} alt={l.jacket.name} loading="lazy"
                     onError={hideOnError} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 hidden items-center justify-center"
                   style={{ display: l.jacket?.thumb_url ? 'none' : 'flex' }}>
                <Shirt size={18} className="text-white/20" />
              </div>
            </div>
            <div className="p-3 space-y-1">
              <p className="text-[8px] tracking-[0.22em] uppercase" style={{ color: 'var(--gold)' }}>
                {l.occasion_label}
              </p>
              <p className="text-xs font-medium tracking-wide leading-snug">{l.title}</p>
              <p className="text-[9px] text-gray-600 leading-snug truncate">{l.jacket?.name}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function LookDetail({ look, onBack }) {
  const rows = [
    ['Jacket', look.jacket?.name],
    ['Trousers', look.trousers],
    ['Shirt', look.shirt],
    ['Footwear', look.footwear],
    ['Accessories', look.accessories],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-4">
      <button onClick={onBack}
        className="flex items-center gap-2 text-[9px] tracking-[0.25em] uppercase text-gray-500 active:opacity-60">
        <ArrowLeft size={13} /> All looks
      </button>

      <div className="relative w-full aspect-[3/4] max-h-[440px] overflow-hidden border border-white/10 bg-white/3">
        {look.jacket?.image_url && (
          <img src={look.jacket.image_url} alt={look.jacket.name}
               onError={hideOnError} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 hidden items-center justify-center"
             style={{ display: look.jacket?.image_url ? 'none' : 'flex' }}>
          <Shirt size={32} className="text-white/20" />
        </div>
      </div>

      <div className="border-b border-white/8 pb-4">
        <p className="text-[9px] tracking-[0.22em] uppercase mb-1" style={{ color: 'var(--gold)' }}>
          {look.occasion_label}
        </p>
        <p className="font-medium tracking-wide text-lg">{look.title}</p>
        {look.why && <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">{look.why}</p>}
      </div>

      <div>
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600 mb-3">The Pieces</p>
        {rows.map(([label, value]) => (
          <div key={label} className="py-3 border-b border-white/8 last:border-0">
            <p className="text-[8px] tracking-[0.22em] uppercase text-gray-600 mb-1">{label}</p>
            <p className="text-sm text-gray-200 leading-snug">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Wardrobe: every piece, searchable ───────────────────────────── */

function Wardrobe() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['wardrobe'], queryFn: getWardrobe, staleTime: 1000 * 60 * 5,
  });
  const [query, setQuery] = useState('');
  const [zoom, setZoom] = useState(null);

  const items = useMemo(() => {
    const all = data?.items || [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((i) =>
      i.name.toLowerCase().includes(q)
      || (i.tags || '').toLowerCase().includes(q)
      || (i.color || '').toLowerCase().includes(q));
  }, [data, query]);

  if (isLoading) return <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10">Loading…</p>;
  if (error) return <p className="text-[10px] tracking-[0.2em] uppercase text-center py-10 text-red-400/80">Could not load wardrobe</p>;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search colour, pattern, fabric"
          autoComplete="off"
          className="w-full bg-white/5 border border-white/10 focus:border-white/30 outline-none
            pl-9 pr-9 py-3 text-sm text-white placeholder:text-gray-600 transition-colors"
        />
        {query && (
          <button onClick={() => setQuery('')} aria-label="Clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 active:opacity-60">
            <X size={14} />
          </button>
        )}
      </div>

      <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">
        {items.length} {query ? 'match' : 'pieces'} · tap to enlarge
      </p>

      <div className="grid grid-cols-3 gap-2">
        {items.map((item) => (
          <button key={item.id} onClick={() => setZoom(item)}
            className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-white/3 active:opacity-70">
            {item.thumb_url && (
              <img src={item.thumb_url} alt={item.name} loading="lazy"
                   onError={hideOnError} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 hidden items-center justify-center"
                 style={{ display: item.thumb_url ? 'none' : 'flex' }}>
              <Shirt size={16} className="text-white/20" />
            </div>
          </button>
        ))}
      </div>

      {zoom && (
        <div onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4">
          <button onClick={() => setZoom(null)} aria-label="Close"
            className="absolute top-5 right-5 p-2 text-white/60 active:opacity-60"><X size={20} /></button>
          <img src={zoom.image_url || zoom.thumb_url} alt={zoom.name}
               className="max-h-[72vh] max-w-full object-contain border border-white/10" />
          <div className="mt-5 text-center max-w-sm">
            <p className="font-medium tracking-wide">{zoom.name}</p>
            {zoom.detail?.notes && (
              <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">{zoom.detail.notes}</p>
            )}
            {zoom.detail?.pairs_with?.length > 0 && (
              <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
                <span className="tracking-[0.2em] uppercase text-[9px]" style={{ color: 'var(--gold)' }}>
                  Pairs with
                </span><br />
                {zoom.detail.pairs_with.join(' · ')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className="flex-shrink-0 px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border transition-all active:opacity-70"
      style={active
        ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
        : { borderColor: 'rgba(255,255,255,0.1)', color: '#5a5a5a' }}>
      {children}
    </button>
  );
}
